import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import api from '../../api/client';
import { Button, StatusBadge } from '../common';
import { formatDate } from '../../utils/formatters';

/**
 * MeetingSummaryModal - Display and email meeting session summary
 */
const MeetingSummaryModal = ({ session, meeting, onClose }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clipboardMessage, setClipboardMessage] = useState(null);

  useEffect(() => {
    loadSummary();
  }, [session.id]);

  const loadSummary = async () => {
    try {
      const data = await api.get(`/sessions/${session.id}/summary`);
      setSummary(data);
    } catch (error) {
      console.error('Failed to load summary:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailSummary = async () => {
    if (!summary) return;

    const participantEmails = summary.participants.map(p => p.email).join(';');
    const meetingDate = new Date(summary.session.startedAt).toLocaleDateString();
    const subject = `${summary.session.meetingName} Summary - ${meetingDate}`;

    // Build Outlook-compatible HTML
    const buildEmailHtml = () => {
      const parts = [];

      // Outer wrapper table
      parts.push('<table width="100%" cellpadding="0" cellspacing="0" border="0" style="font-family:Arial,sans-serif;color:#404040;"><tr><td>');
      parts.push('<table width="800" cellpadding="0" cellspacing="0" border="0" align="center">');

      // Header — white card with blue top accent (matches preview summary-section)
      parts.push('<tr><td style="padding-bottom:16px;">');
      parts.push('<table width="100%" cellpadding="0" cellspacing="0" border="0" style="border:1px solid #e0e4ef;">');
      parts.push('<tr><td bgcolor="#4f6ef7" style="height:4px;font-size:0;line-height:0;">&nbsp;</td></tr>');
      parts.push('<tr><td bgcolor="#ffffff" style="padding:20px 25px;">');
      parts.push('<h1 style="margin:0 0 6px 0;padding:0;font-size:20px;font-weight:700;color:#1a1d2e;font-family:Arial,sans-serif;">' + summary.session.meetingName + '</h1>');
      parts.push('<p style="margin:0;padding:0;font-size:13px;color:#6b7280;">Started: ' + new Date(summary.session.startedAt).toLocaleString());
      if (summary.session.endedAt) {
        parts.push(' &nbsp;|&nbsp; Ended: ' + new Date(summary.session.endedAt).toLocaleString());
      }
      parts.push('</p></td></tr></table>');
      parts.push('</td></tr>');

      // Body
      parts.push('<tr><td>');
      parts.push('<table width="100%" cellpadding="0" cellspacing="0" border="0" style="border-left:1px solid #e0e4ef;border-right:1px solid #e0e4ef;border-bottom:1px solid #e0e4ef;">');
      parts.push('<tr><td bgcolor="#ffffff" style="padding:20px 25px;">');

      // Topics section
      if (summary.discussedTopics.length > 0) {
        parts.push('<h2 style="margin:0 0 16px 0;padding:0;font-size:17px;color:#1a1d2e;font-family:Arial,sans-serif;">&#128203; Topics Discussed (' + summary.discussedTopics.length + ')</h2>');

        summary.discussedTopics.forEach((topic) => {
          parts.push('<table width="100%" cellpadding="0" cellspacing="0" border="0" style="margin-bottom:20px;border:1px solid #e0e4ef;"><tr>');
          parts.push('<td width="4" bgcolor="#4f6ef7" style="font-size:0;line-height:0;">&nbsp;</td>');
          parts.push('<td>');
          parts.push('<table width="100%" cellpadding="0" cellspacing="0" border="0">');
          parts.push('<tr><td bgcolor="#4f6ef7" style="padding:10px 15px;">');
          parts.push('<span style="font-weight:bold;font-size:15px;color:#ffffff;">' + topic.title + '</span><br>');
          parts.push('<span style="font-size:12px;color:#c7d2fe;">Submitted by ' + (topic.creatorName || 'Unknown') + '</span>');
          parts.push('</td></tr></table>');
          parts.push('<table width="100%" cellpadding="0" cellspacing="0" border="0">');
          parts.push('<tr><td style="padding:12px 15px;">');

          if (topic.discussionNotes) {
            parts.push('<table width="100%" cellpadding="0" cellspacing="0" border="0" style="margin-bottom:12px;"><tr>');
            parts.push('<td width="3" bgcolor="#4f6ef7" style="font-size:0;line-height:0;">&nbsp;</td>');
            parts.push('<td bgcolor="#e8ecff" style="padding:10px 12px;font-size:13px;">');
            parts.push('<strong style="color:#1a1d2e;">Discussion Notes:</strong><br>');
            parts.push(topic.discussionNotes.replace(/\n/g, '<br>'));
            parts.push('</td></tr></table>');
          }

          if (topic.todos && topic.todos.length > 0) {
            parts.push('<p style="margin:8px 0 6px 0;padding:0;font-size:14px;font-weight:bold;color:#1a1d2e;">Action Items (' + topic.todos.length + '):</p>');
            parts.push('<table width="100%" cellpadding="0" cellspacing="0" border="0" style="border:1px solid #e0e4ef;">');
            parts.push('<tr>');
            parts.push('<td bgcolor="#f4f6fb" style="padding:8px 10px;border-bottom:2px solid #4f6ef7;font-weight:bold;font-size:13px;color:#1a1d2e;">Task</td>');
            parts.push('<td bgcolor="#f4f6fb" style="padding:8px 10px;border-bottom:2px solid #4f6ef7;font-weight:bold;font-size:13px;color:#1a1d2e;">Assigned To</td>');
            parts.push('<td bgcolor="#f4f6fb" style="padding:8px 10px;border-bottom:2px solid #4f6ef7;font-weight:bold;font-size:13px;color:#1a1d2e;" width="100">Due Date</td>');
            parts.push('<td bgcolor="#f4f6fb" style="padding:8px 10px;border-bottom:2px solid #4f6ef7;font-weight:bold;font-size:13px;color:#1a1d2e;" width="90">Status</td>');
            parts.push('</tr>');

            topic.todos.forEach(todo => {
              const assigneeNames = (todo.assignees || []).map(a => a.firstName + ' ' + a.lastName).join(', ');
              const isOverdue = todo.dueDate && new Date(todo.dueDate) < new Date() && todo.status !== 'Done';
              parts.push('<tr>');
              parts.push('<td style="padding:8px 10px;border-bottom:1px solid #e0e4ef;font-size:13px;">' + todo.title + '</td>');
              parts.push('<td style="padding:8px 10px;border-bottom:1px solid #e0e4ef;font-size:13px;">' + (assigneeNames || 'Unassigned') + '</td>');
              parts.push('<td style="padding:8px 10px;border-bottom:1px solid #e0e4ef;font-size:13px;' + (isOverdue ? 'color:#dc2626;font-weight:bold;' : '') + '">' + (todo.dueDate ? new Date(todo.dueDate).toLocaleDateString() : '-') + '</td>');
              parts.push('<td style="padding:8px 10px;border-bottom:1px solid #e0e4ef;font-size:13px;"><span style="background:#e8ecff;color:#1a1d2e;padding:2px 8px;font-size:11px;border:1px solid #4f6ef7;">' + todo.status + '</span></td>');
              parts.push('</tr>');
            });

            parts.push('</table>');
          }

          parts.push('</td></tr></table>');
          parts.push('</td></tr></table>');
        });
      }

      // Carryover tasks
      if (summary.carryoverTodos && summary.carryoverTodos.length > 0) {
        parts.push('<table width="100%" cellpadding="0" cellspacing="0" border="0" style="margin-top:20px;border:1px solid #E5BA73;">');
        parts.push('<tr><td bgcolor="#FEF3C7" style="padding:15px;">');
        parts.push('<h3 style="margin:0 0 12px 0;padding:0;font-size:15px;color:#1a1d2e;font-family:Arial,sans-serif;">&#9888;&#65039; Incomplete Tasks (' + summary.carryoverTodos.length + ')</h3>');
        parts.push('<table width="100%" cellpadding="0" cellspacing="0" border="0" style="border:1px solid #E5BA73;">');
        parts.push('<tr>');
        parts.push('<td bgcolor="#F5E6C8" style="padding:8px 10px;border-bottom:2px solid #E5BA73;font-weight:bold;font-size:13px;color:#1a1d2e;">Task</td>');
        parts.push('<td bgcolor="#F5E6C8" style="padding:8px 10px;border-bottom:2px solid #E5BA73;font-weight:bold;font-size:13px;color:#1a1d2e;">From Topic</td>');
        parts.push('<td bgcolor="#F5E6C8" style="padding:8px 10px;border-bottom:2px solid #E5BA73;font-weight:bold;font-size:13px;color:#1a1d2e;">Assigned To</td>');
        parts.push('<td bgcolor="#F5E6C8" style="padding:8px 10px;border-bottom:2px solid #E5BA73;font-weight:bold;font-size:13px;color:#1a1d2e;" width="100">Due Date</td>');
        parts.push('<td bgcolor="#F5E6C8" style="padding:8px 10px;border-bottom:2px solid #E5BA73;font-weight:bold;font-size:13px;color:#1a1d2e;" width="90">Status</td>');
        parts.push('</tr>');

        summary.carryoverTodos.forEach(todo => {
          const assigneeNames = (todo.assignees || []).map(a => a.firstName + ' ' + a.lastName).join(', ');
          const isOverdue = todo.dueDate && new Date(todo.dueDate) < new Date() && todo.status !== 'Done';
          parts.push('<tr>');
          parts.push('<td bgcolor="#FEF3C7" style="padding:8px 10px;border-bottom:1px solid #f0dfa0;font-size:13px;">' + todo.title + '</td>');
          parts.push('<td bgcolor="#FEF3C7" style="padding:8px 10px;border-bottom:1px solid #f0dfa0;font-size:13px;">' + (todo.topicTitle || '-') + '</td>');
          parts.push('<td bgcolor="#FEF3C7" style="padding:8px 10px;border-bottom:1px solid #f0dfa0;font-size:13px;">' + (assigneeNames || 'Unassigned') + '</td>');
          parts.push('<td bgcolor="#FEF3C7" style="padding:8px 10px;border-bottom:1px solid #f0dfa0;font-size:13px;' + (isOverdue ? 'color:#dc2626;font-weight:bold;' : '') + '">' + (todo.dueDate ? new Date(todo.dueDate).toLocaleDateString() : '-') + '</td>');
          parts.push('<td bgcolor="#FEF3C7" style="padding:8px 10px;border-bottom:1px solid #f0dfa0;font-size:13px;"><span style="background:#e8ecff;color:#1a1d2e;padding:2px 8px;font-size:11px;border:1px solid #4f6ef7;">' + todo.status + '</span></td>');
          parts.push('</tr>');
        });

        parts.push('</table>');
        parts.push('</td></tr></table>');
      }

      if (summary.discussedTopics.length === 0 && (!summary.carryoverTodos || summary.carryoverTodos.length === 0)) {
        parts.push('<p style="text-align:center;color:#666;padding:20px 0;">No topics were discussed in this session.</p>');
      }

      parts.push('</td></tr></table>');

      // Footer
      parts.push('<tr><td>');
      parts.push('<table width="100%" cellpadding="0" cellspacing="0" border="0"><tr>');
      parts.push('<td bgcolor="#1a1d2e" style="padding:12px 25px;text-align:center;font-size:11px;color:#c7d2fe;">');
      parts.push('Meeting Summary generated by MeetingManager');
      parts.push('</td></tr></table>');
      parts.push('</td></tr>');

      parts.push('</table>');
      parts.push('</td></tr></table>');

      return parts.join('');
    };

    const htmlContent = buildEmailHtml();

    // Try multiple clipboard methods
    const copyToClipboard = async () => {
      // Method 1: Modern Clipboard API
      if (navigator.clipboard && typeof ClipboardItem !== 'undefined') {
        try {
          const blob = new Blob([htmlContent], { type: 'text/html' });
          const clipboardItem = new ClipboardItem({ 'text/html': blob });
          await navigator.clipboard.write([clipboardItem]);
          return true;
        } catch (err) {
          console.log('ClipboardItem method failed:', err);
        }
      }

      // Method 2: execCommand fallback
      try {
        const container = document.createElement('div');
        container.innerHTML = htmlContent;
        container.style.position = 'fixed';
        container.style.left = '-9999px';
        container.style.top = '0';
        document.body.appendChild(container);

        const range = document.createRange();
        range.selectNodeContents(container);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);

        const success = document.execCommand('copy');
        selection.removeAllRanges();
        document.body.removeChild(container);

        if (success) return true;
      } catch (err) {
        console.log('execCommand method failed:', err);
      }

      // Method 3: Plain text fallback
      try {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = htmlContent;
        const plainText = tempDiv.textContent || tempDiv.innerText;
        await navigator.clipboard.writeText(plainText);
        return 'plain';
      } catch (err) {
        console.log('Plain text method failed:', err);
      }

      return false;
    };

    const result = await copyToClipboard();

    if (result === true) {
      setClipboardMessage({ text: '✓ Summary copied to clipboard! Paste (Ctrl+V / Cmd+V) into your email body.', type: 'success' });
    } else if (result === 'plain') {
      setClipboardMessage({ text: '✓ Summary copied as plain text (rich formatting requires HTTPS). Paste into your email body.', type: 'success' });
    } else {
      setClipboardMessage({ text: '⚠️ Could not copy to clipboard (requires HTTPS). You can manually copy the summary from the modal.', type: 'error' });
    }

    // Open mailto link
    const mailtoLink = `mailto:${participantEmails}?subject=${encodeURIComponent(subject)}`;
    window.location.href = mailtoLink;
  };

  if (loading) {
    return (
      <div className="modal-overlay">
        <div className="modal">
          <div className="modal-body">
            <p>Loading summary...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="modal-overlay">
        <div className="modal">
          <div className="modal-header">
            <h2 className="modal-title">Error</h2>
            <button className="modal-close" onClick={onClose}>×</button>
          </div>
          <div className="modal-body">
            <p>Failed to load meeting summary.</p>
          </div>
          <div className="modal-footer">
            <Button variant="secondary" onClick={onClose}>Close</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: '800px' }}>
        <div className="modal-header">
          <h2 className="modal-title">Meeting Summary</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="summary-section">
            <h3>{summary.session.meetingName}</h3>
            <p className="card-meta">
              Started: {new Date(summary.session.startedAt).toLocaleString()}
              <br />
              {summary.session.endedAt && `Ended: ${new Date(summary.session.endedAt).toLocaleString()}`}
            </p>
          </div>

          {summary.discussedTopics.length > 0 && (
            <>
              <h3 style={{ color: 'var(--color-text)', marginBottom: '1rem' }}>
                📋 Topics Discussed ({summary.discussedTopics.length})
              </h3>
              {summary.discussedTopics.map(topic => (
                <div 
                  key={topic.id} 
                  className="summary-section" 
                  style={{ borderLeft: '4px solid var(--color-primary)', padding: '0' }}
                >
                  <div style={{
                    background: 'var(--color-primary)',
                    color: 'white',
                    padding: '0.75rem 1rem',
                    borderRadius: 'calc(var(--radius-lg) - 1px) calc(var(--radius-lg) - 1px) 0 0',
                    marginBottom: '0.75rem'
                  }}>
                    <div style={{ fontWeight: 'bold' }}>{topic.title}</div>
                    <div style={{ fontSize: '0.8rem', opacity: 0.9 }}>
                      Submitted by {topic.creatorName}
                    </div>
                  </div>

                  {topic.discussionNotes && (
                    <div style={{
                      padding: '0.75rem 1rem',
                      background: '#e8ecff',
                      borderRadius: '4px',
                      marginBottom: '0.75rem',
                      borderLeft: '3px solid var(--color-primary)',
                      whiteSpace: 'pre-wrap',
                      margin: '0 1rem 0.75rem'
                    }}>
                      <strong>Discussion Notes:</strong>
                      <br />
                      {topic.discussionNotes}
                    </div>
                  )}

                  {topic.todos && topic.todos.length > 0 && (
                    <div style={{ padding: '0 1rem 1rem' }}>
                      <strong style={{ fontSize: '0.9rem', color: 'var(--color-text)' }}>Action Items ({topic.todos.length}):</strong>
                      <table className="data-table" style={{ marginTop: '0.5rem' }}>
                        <thead>
                          <tr>
                            <th>Task</th>
                            <th>Assigned To</th>
                            <th>Due Date</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {topic.todos.map(todo => {
                            const isOverdue = todo.dueDate && new Date(todo.dueDate) < new Date() && todo.status !== 'Done';
                            return (
                              <tr key={todo.id}>
                                <td>{todo.title}</td>
                                <td>
                                  {(todo.assignees || []).map(a => `${a.firstName} ${a.lastName}`).join(', ') || 'Unassigned'}
                                </td>
                                <td style={{
                                  color: isOverdue ? '#dc2626' : 'inherit',
                                  fontWeight: isOverdue ? 'bold' : 'normal'
                                }}>
                                  {formatDate(todo.dueDate)}
                                </td>
                                <td>
                                  <StatusBadge status={todo.status} type="todo" />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </>
          )}

          {summary.carryoverTodos && summary.carryoverTodos.length > 0 && (
            <div className="carryover-tasks">
              <h4>⚠️ Incomplete Tasks ({summary.carryoverTodos.length})</h4>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Task</th>
                    <th>From Topic</th>
                    <th>Assigned To</th>
                    <th>Due Date</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.carryoverTodos.map(todo => {
                    const isOverdue = todo.dueDate && new Date(todo.dueDate) < new Date() && todo.status !== 'Done';
                    return (
                      <tr key={todo.id} style={{ background: '#fffbeb' }}>
                        <td>{todo.title}</td>
                        <td>{todo.topicTitle}</td>
                        <td>
                          {(todo.assignees || []).map(a => `${a.firstName} ${a.lastName}`).join(', ') || 'Unassigned'}
                        </td>
                        <td style={{
                          color: isOverdue ? '#dc2626' : 'inherit',
                          fontWeight: isOverdue ? 'bold' : 'normal'
                        }}>
                          {formatDate(todo.dueDate)}
                        </td>
                        <td>
                          <StatusBadge status={todo.status} type="todo" />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className="modal-footer">
          {clipboardMessage && (
            <div
              className={`alert ${clipboardMessage.type === 'success' ? 'alert-success' : 'alert-error'}`}
              style={{ flex: 1, margin: 0, fontSize: '13px' }}
            >
              {clipboardMessage.text}
            </div>
          )}
          <Button variant="primary" onClick={handleEmailSummary}>
            📧 Email Summary
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

MeetingSummaryModal.propTypes = {
  session: PropTypes.object.isRequired,
  meeting: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default MeetingSummaryModal;
